import React, { useState, useEffect } from "react";
import { resolveProfileImageUrlFromValue } from "../../../api/profile/profile-image-service";
import { pickChatColor } from "../../../api/profile/chat-color-service";

function getReadableTextColor(hexColor) {
  const hex = String(hexColor || "").replace("#", "");
  if (hex.length !== 6) return "#ffffff";
  const r = Number.parseInt(hex.slice(0, 2), 16);
  const g = Number.parseInt(hex.slice(2, 4), 16);
  const b = Number.parseInt(hex.slice(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 145 ? "#111827" : "#ffffff";
}

function ProfileAvatarBase({ user, name, className = "" }) {
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const hasCustomClass = Boolean(String(className || "").trim());
  const imageClassName = hasCustomClass
    ? className
    : "communityBrowse__cardImg";
  const fallbackClassName = hasCustomClass
    ? `communityBrowse__cardImgFallback ${className}`
    : "communityBrowse__cardImgFallback";
  const avatarSeed =
    user?.profilePicture ||
    user?.avatar ||
    user?.image ||
    user?.profile?.profilePicture ||
    "";
  const hasResolvedUser = user !== null && user !== undefined;
  const hasAvatarSeed = Boolean(String(avatarSeed || "").trim());
  const fallbackColor = pickChatColor(user);
  const fallbackStyle = {
    backgroundColor: fallbackColor,
    backgroundImage: "none",
    color: getReadableTextColor(fallbackColor),
  };
  const fallbackInitial = String(name || "").trim().charAt(0).toUpperCase() || "U";

  useEffect(() => {
    let cancelled = false;

    const fetchSignedUrl = async () => {
      try {
        const nextUrl = await resolveProfileImageUrlFromValue(avatarSeed);
        if (!cancelled) {
          setImageUrl(nextUrl);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (!hasAvatarSeed) {
      setImageUrl("");
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    setImageUrl("");
    fetchSignedUrl();

    return () => {
      cancelled = true;
    };
  }, [avatarSeed, hasAvatarSeed]);

  if (!hasResolvedUser) {
    return <div className={imageClassName} aria-hidden="true" />;
  }

  if (loading) {
    // Show the deterministic colored-initial fallback immediately (it needs no
    // network) and swap to the image once the presigned URL resolves, rather
    // than rendering a blank box during the per-avatar presign round-trip.
    return (
      <div className={fallbackClassName} style={fallbackStyle}>
        {fallbackInitial}
      </div>
    );
  }

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={name}
        className={imageClassName}
        onError={() => setImageUrl("")}
      />
    );
  }

  return (
    <div className={fallbackClassName} style={fallbackStyle}>
      {fallbackInitial}
    </div>
  );
}

// Memoized so unrelated parent state changes (e.g. opening a compose sheet,
// applying filters) don't re-render and re-resolve every visible avatar card.
// Callers pass stable user objects (paginated/memoized lists), so the default
// shallow prop comparison is effective.
export const ProfileAvatar = React.memo(ProfileAvatarBase);
